import type { AshtakootaResult, KootaScore, Planet } from '@astrally/shared';
import { NAKSHATRA_NAMES } from '@astrally/shared';
import { signLord } from '../core/zodiac.js';

/**
 * Ashtakoota (guna milan) — the classical 36-point Moon-based compatibility.
 *
 * Inputs are Moon rashi (sidereal sign 0-11) and nakshatra index (0-26) for
 * two people. Deterministic and cheap: used by the matching engine as the
 * jyotishCompat signal (never as proof of anything — see safety copy).
 *
 * Simplifications, documented in docs/ASTROLOGY_ENGINE.md:
 * - Varna is classically directional; we compute it for (A,B) order.
 * - Vashya uses whole-sign group assignment.
 * - Yoni uses same=4 / enemy=0 / otherwise 2 (full 14×14 matrix is a later
 *   refinement).
 */

export interface MoonProfile {
  rashi: number; // 0-11 sidereal
  nakshatra: number; // 0-26
}

// --- Varna -----------------------------------------------------------------
const VARNA: number[] = [
  // rank: 3 Brahmin, 2 Kshatriya, 1 Vaishya, 0 Shudra
  2, // Aries
  1, // Taurus
  0, // Gemini
  3, // Cancer
  2, // Leo
  1, // Virgo
  0, // Libra
  3, // Scorpio
  2, // Sagittarius
  1, // Capricorn
  0, // Aquarius
  3, // Pisces
];

// --- Vashya ----------------------------------------------------------------
// Exported (with the tables below) so the South Indian porutham system in
// ./porutham.ts scores off the SAME data. The two systems band the raw values
// differently, but a divergence in the tables themselves would be a bug.
export type VashyaGroup = 'chatushpada' | 'manava' | 'jalachara' | 'vanachara' | 'keeta';
export const VASHYA: VashyaGroup[] = [
  'chatushpada', // Aries
  'chatushpada', // Taurus
  'manava', // Gemini
  'jalachara', // Cancer
  'vanachara', // Leo
  'manava', // Virgo
  'manava', // Libra
  'keeta', // Scorpio
  'chatushpada', // Sagittarius
  'jalachara', // Capricorn
  'manava', // Aquarius
  'jalachara', // Pisces
];
export const VASHYA_SCORE: Record<VashyaGroup, Record<VashyaGroup, number>> = {
  chatushpada: { chatushpada: 2, manava: 1, jalachara: 1, vanachara: 0, keeta: 1 },
  manava: { chatushpada: 1, manava: 2, jalachara: 0.5, vanachara: 0, keeta: 1 },
  jalachara: { chatushpada: 1, manava: 0.5, jalachara: 2, vanachara: 0, keeta: 1 },
  vanachara: { chatushpada: 0, manava: 0, jalachara: 0, vanachara: 2, keeta: 0 },
  keeta: { chatushpada: 1, manava: 1, jalachara: 1, vanachara: 0, keeta: 2 },
};

// --- Yoni ------------------------------------------------------------------
export type YoniAnimal =
  | 'horse' | 'elephant' | 'sheep' | 'serpent' | 'dog' | 'cat' | 'rat' | 'cow'
  | 'buffalo' | 'tiger' | 'deer' | 'monkey' | 'mongoose' | 'lion';
export const YONI: YoniAnimal[] = [
  'horse', 'elephant', 'sheep', 'serpent', 'serpent', 'dog', 'cat', 'sheep', 'cat',
  'rat', 'rat', 'cow', 'buffalo', 'tiger', 'buffalo', 'tiger', 'deer', 'deer',
  'dog', 'monkey', 'mongoose', 'monkey', 'lion', 'horse', 'lion', 'cow', 'elephant',
];
const YONI_ENEMIES: [YoniAnimal, YoniAnimal][] = [
  ['cow', 'tiger'],
  ['horse', 'buffalo'],
  ['elephant', 'lion'],
  ['dog', 'deer'],
  ['serpent', 'mongoose'],
  ['monkey', 'sheep'],
  ['cat', 'rat'],
];
/** 4 = same yoni, 0 = classical enemy pair, 2 = neither. */
export function yoniScore(a: YoniAnimal, b: YoniAnimal): number {
  if (a === b) return 4;
  const enemy = YONI_ENEMIES.some(
    ([x, y]) => (x === a && y === b) || (x === b && y === a),
  );
  return enemy ? 0 : 2;
}

// --- Graha Maitri ----------------------------------------------------------
export type Relation = 'friend' | 'neutral' | 'enemy';
const FRIENDSHIP: Record<string, { friends: Planet[]; enemies: Planet[] }> = {
  sun: { friends: ['moon', 'mars', 'jupiter'], enemies: ['venus', 'saturn'] },
  moon: { friends: ['sun', 'mercury'], enemies: [] },
  mars: { friends: ['sun', 'moon', 'jupiter'], enemies: ['mercury'] },
  mercury: { friends: ['sun', 'venus'], enemies: ['moon'] },
  jupiter: { friends: ['sun', 'moon', 'mars'], enemies: ['mercury', 'venus'] },
  venus: { friends: ['mercury', 'saturn'], enemies: ['sun', 'moon'] },
  saturn: { friends: ['mercury', 'venus'], enemies: ['sun', 'moon', 'mars'] },
};
/** Naisargika (natural) relation of `a` towards `b` — not symmetric. */
export function relation(a: Planet, b: Planet): Relation {
  if (a === b) return 'friend';
  const f = FRIENDSHIP[a];
  if (!f) return 'neutral';
  if (f.friends.includes(b)) return 'friend';
  if (f.enemies.includes(b)) return 'enemy';
  return 'neutral';
}
function maitriScore(lordA: Planet, lordB: Planet): number {
  if (lordA === lordB) return 5;
  const ab = relation(lordA, lordB);
  const ba = relation(lordB, lordA);
  const pair = [ab, ba].sort().join('-');
  switch (pair) {
    case 'friend-friend':
      return 5;
    case 'friend-neutral':
      return 4;
    case 'neutral-neutral':
      return 3;
    case 'enemy-friend':
      return 1;
    case 'enemy-neutral':
      return 0.5;
    default:
      return 0; // enemy-enemy
  }
}

// --- Gana ------------------------------------------------------------------
export type Gana = 'deva' | 'manushya' | 'rakshasa';
export const GANA: Gana[] = [
  'deva', 'manushya', 'rakshasa', 'manushya', 'deva', 'manushya', 'deva', 'deva',
  'rakshasa', 'rakshasa', 'manushya', 'manushya', 'deva', 'rakshasa', 'deva',
  'rakshasa', 'deva', 'rakshasa', 'rakshasa', 'manushya', 'manushya', 'deva',
  'rakshasa', 'rakshasa', 'manushya', 'manushya', 'deva',
];
function ganaScore(a: Gana, b: Gana): number {
  if (a === b) return 6;
  const pair = [a, b].sort().join('-');
  if (pair === 'deva-manushya') return 5;
  if (pair === 'deva-rakshasa') return 1;
  return 0; // manushya-rakshasa
}

// --- Nadi ------------------------------------------------------------------
type Nadi = 'adi' | 'madhya' | 'antya';
const NADI_CYCLE: Nadi[] = ['adi', 'madhya', 'antya', 'antya', 'madhya', 'adi', 'adi', 'madhya', 'antya'];
function nadiOf(nakshatra: number): Nadi {
  return NADI_CYCLE[nakshatra % 9];
}

// --- Tara ------------------------------------------------------------------
/**
 * Inclusive nakshatra count from one star to another: same star = 1, next = 2,
 * wrapping at 27. Mirrors `signCount` in core/zodiac.ts. The single definition
 * of the counting convention — porutham.ts counts with this too.
 */
export function nakshatraCount(fromNak: number, toNak: number): number {
  return ((toNak - fromNak + 27) % 27) + 1;
}

/** Tara (1-9) of `toNak` reckoned from `fromNak`. */
export function taraOf(fromNak: number, toNak: number): number {
  const count = nakshatraCount(fromNak, toNak);
  return count % 9 === 0 ? 9 : count % 9;
}

/** Taras 3 (Vipat), 5 (Pratyak) and 7 (Naidhana) are the strained ones. */
export function taraFavourable(fromNak: number, toNak: number): boolean {
  return ![3, 5, 7].includes(taraOf(fromNak, toNak));
}

// --- Bhakoot ---------------------------------------------------------------
function bhakootScore(rashiA: number, rashiB: number): number {
  const d = ((rashiB - rashiA + 12) % 12) + 1;
  const pair = [d, 14 - d].sort((x, y) => x - y).join('-');
  return pair === '2-12' || pair === '5-9' || pair === '6-8' ? 0 : 7;
}

export function computeAshtakoota(a: MoonProfile, b: MoonProfile): AshtakootaResult {
  const kootas: KootaScore[] = [];

  const varnaPts = VARNA[a.rashi] >= VARNA[b.rashi] ? 1 : 0;
  kootas.push({
    koota: 'varna',
    points: varnaPts,
    max: 1,
    note: 'Work/temperament class of the Moon signs (directional classical rule).',
  });

  const vashyaPts = (VASHYA_SCORE[VASHYA[a.rashi]][VASHYA[b.rashi]] +
    VASHYA_SCORE[VASHYA[b.rashi]][VASHYA[a.rashi]]) / 2;
  kootas.push({
    koota: 'vashya',
    points: vashyaPts,
    max: 2,
    note: 'Mutual influence groups of the Moon signs.',
  });

  const taraPts =
    (taraFavourable(a.nakshatra, b.nakshatra) ? 1.5 : 0) +
    (taraFavourable(b.nakshatra, a.nakshatra) ? 1.5 : 0);
  kootas.push({
    koota: 'tara',
    points: taraPts,
    max: 3,
    note: `Nakshatra count between ${NAKSHATRA_NAMES[a.nakshatra]} and ${NAKSHATRA_NAMES[b.nakshatra]}.`,
  });

  const yoniPts = yoniScore(YONI[a.nakshatra], YONI[b.nakshatra]);
  kootas.push({
    koota: 'yoni',
    points: yoniPts,
    max: 4,
    note: `Yoni animals: ${YONI[a.nakshatra]} & ${YONI[b.nakshatra]}.`,
  });

  const lordA = signLord(a.rashi);
  const lordB = signLord(b.rashi);
  kootas.push({
    koota: 'grahaMaitri',
    points: maitriScore(lordA, lordB),
    max: 5,
    note: `Moon-sign lords: ${lordA} & ${lordB}.`,
  });

  kootas.push({
    koota: 'gana',
    points: ganaScore(GANA[a.nakshatra], GANA[b.nakshatra]),
    max: 6,
    note: `Ganas: ${GANA[a.nakshatra]} & ${GANA[b.nakshatra]}.`,
  });

  kootas.push({
    koota: 'bhakoot',
    points: bhakootScore(a.rashi, b.rashi),
    max: 7,
    note: 'Relative Moon-sign positions.',
  });

  const nadiPts = nadiOf(a.nakshatra) === nadiOf(b.nakshatra) ? 0 : 8;
  kootas.push({
    koota: 'nadi',
    points: nadiPts,
    max: 8,
    note: `Nadis: ${nadiOf(a.nakshatra)} & ${nadiOf(b.nakshatra)}.`,
  });

  const total = kootas.reduce((s, k) => s + k.points, 0);
  return { total, max: 36, kootas };
}
